import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Clock, Wrench, MapPin } from "lucide-react";

const Projects = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const caseStudies = [
    {
      id: 1,
      title: "Office Refurbishment",
      client: "Aspire Pharma Ltd",
      location: "Petersfield",
      description:
        "Refreshed an outdated office with new glazed partitions, suspended ceilings, LED lighting and modern flooring. Improved acoustic privacy and energy efficiency with minimal disruption to daily operations.",
      services: ["Decorating", "Furniture", "Project management"],
      duration: "10 weeks",
      icon: Building2,
    },
    {
      id: 2,
      title: "Warehouse & Office Fit-out",
      client: "British Medical Auctions Ltd",
      location: "Bracknell",
      description:
        "Converted unused warehouse space into a functional office hub, including space planning, air conditioning installation and a durable flooring system suitable for high traffic.",
      services: [
        "Partitioning",
        "Suspended Ceilings",
        "Decorating",
        "Flooring",
        "Furniture",
        "Space planning",
        "Project management",
      ],
      duration: "18 weeks",
      icon: Building2,
    },
    {
      id: 3,
      title: "Reactive Maintenance",
      client: "Pharmagraph",
      location: "Wokingham",
      description:
        "Ongoing monthly support including electrical repairs, painting, data point additions and minor partitioning work, ensuring the facility remains compliant and safe.",
      services: ["Facilities management"],
      duration: "Ongoing",
      icon: Wrench,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Our Projects
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6 text-lg text-gray-700"
          >
            <p>
              Sandhurst Interiors is a family run business providing office and
              commercial refurbishment and fit out services since 1988
              throughout London, the South East of England and beyond.
            </p>
            <p>
              Our professional, dedicated and skilled team work closely with our
              clients to ensure their project, regardless of size, is managed
              and delivered to meet both their expectations and needs. That's
              why over three quarters of our projects are from returning
              clients.
            </p>
            <p>
              We provide complete office fit-out and refurbishment solutions,
              specialising in office partitioning and suspended ceiling systems,
              working directly for end-user clients and with main contractors,
              on projects of all sizes. Whatever your requirements, we have the
              expertise and vision to deliver.
            </p>
            <p>
              At Sandhurst Interiors we provide a comprehensive range of office
              and commercial refurbishment and fit out services. We have an
              extensive portfolio of successful projects ranging from a single
              office to complete buildings, in both the private and public
              sector, including schools and hospitals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {caseStudies.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                }}
                className="card group hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-200 transition-colors duration-300">
                  <project.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {project.title}
                </h3>
                {project.client && (
                  <div className="text-gray-700 font-medium mb-2">
                    <span>{project.client}</span>
                  </div>
                )}
                {project.location && (
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="w-4 h-4 mr-2" />
                    <span>{project.location}</span>
                  </div>
                )}
                {project.description && (
                  <p className="text-gray-600 mb-6">{project.description}</p>
                )}

                <div className="border-t border-gray-200 pt-6 space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      Key Services:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {project.services.map((service, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">
                      Duration: {project.duration}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to start your project?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Speak with our team today about transforming your office,
              warehouse or facility.
            </p>
            <div className="flex justify-center">
              <Link
                to="/contact#contact-form"
                onClick={() => {
                  sessionStorage.setItem("scrollToContactForm", "true");
                }}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 text-center"
              >
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
